<template>

  <v-layout column fill-height>
    <div id="map"  style="height:100%; width:100%; position: relative" @click.capture="" ref="OLMap" class="map">
      <v-tooltip left color="transparent">
          <v-icon v-if="$store.state.topology.mapSourceOnline" color="secondary" style="z-index: 1">public</v-icon>
          <v-icon v-if="!$store.state.topology.mapSourceOnline" color="grey" style="z-index: 1">vpn_lock</v-icon>
        </v-btn>
        <span v-if="$store.state.topology.mapSourceOnline" class="blacktext--text">{{ $t('online map source') }}</span>
        <span v-if="!$store.state.topology.mapSourceOnline" class="blacktext--text">{{ $t('offline map source') }}</span>
      </v-tooltip>
     
      <div
        v-show="editMapCenter"
        @mouseover="" 
        @mouseout=""
        @click.stop="" 
        class="centerIcon" 
        ref="centerIcon">
      </div>

      <div v-show="popupData" ref="connection-popup" class="ol-popup">
        <div ref="popup-content" class="popup-content">
          <popup-card
            v-if="popupData"
            :data="popupData"
            @close="() => closePopup()"
          />
        </div>
      </div>
    </div>

    <v-flex v-if="mode == 'location'"xs1>
      <v-layout fill-height justify-space-between class="grey lighten-5" py-1>
        <v-layout pb-2>
          <v-flex xs4 shrink px-0 >
            <v-text-field
              :value="$t('latitude')"
              light
              outline
              flat
              readonly
              disabled
              class="backgrey"
              hide-details
              :rows="1"
              color="white"
            >
            </v-text-field>
          </v-flex>

          <v-flex xs8 shrink px-0 >
            <v-text-field
              v-model="center[0]"
              light
              auto-grow
              outline
              :readonly="!edit"
              class="white"
              hide-details
              :rows="1"
              color="accent"
            >         
            </v-text-field>
          </v-flex>
        </v-layout>

        <v-layout px-1 pb-2>
          <v-flex xs4 shrink px-0 >
            <v-text-field
              :value="$t('longitude')"
              light
              outline
              flat
              readonly
              disabled
              class="backgrey"
              hide-details
              :rows="1"
              color="white"
            >
            </v-text-field>
          </v-flex>

          <v-flex xs8 shrink px-0 >
            <v-text-field
              v-model="center[1]"
              light
              auto-grow
              outline
              :readonly="!edit"
              class="white"
              hide-details
              :rows="1"
              color="accent"
            >         
            </v-text-field>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script lang="ts">
import Vue from 'vue'
import { getURL } from '@/nms/plugins/bridge.js'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapMutations, mapActions } = createNamespacedHelpers('schemas')
import popupCard from '@/nms/pages/topology/popup-card.vue'
import scrollArea from '@/nms/components/partials/scrollarea.vue'
import { parse } from 'postsvg'
import render from 'posthtml-render'
import XYZ from 'ol/source/XYZ'
import 'ol/ol.css'
import Feature from 'ol/Feature'
import Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import Point from 'ol/geom/Point'
import VectorSource from 'ol/source/Vector'
import Cluster from 'ol/source/Cluster'
import View from 'ol/View'
import { GeoJSON } from 'ol/format'
import * as interaction from 'ol/interaction'
import { Icon, Circle, Fill, Stroke, Style, Text } from 'ol/style'
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import { fromLonLat, toLonLat } from 'ol/proj'
import Polygon from 'ol/geom/Polygon'
import * as turf from '@turf/turf'

// Типы
interface Coordinates {
  longitude: number
  latitude: number
}

interface LocationItem {
  $id?: string
  name?: string
  type?: string
  location_type?: string
  condition_type?: string
  coordinates?: Coordinates
  latitude?: number
  longitude?: number
  area?: any[]
  area_color?: string
  [key: string]: any
}

interface ConnectionItem {
  $id?: string
  template?: string
  locations?: [LocationItem, LocationItem]
  [key: string]: any
}

interface FilterState {
  channelTypes: any[]
  locationTypes: string[]
  objectTypes: string[]
  objectSubtypes: any[]
  locationSubtypes: any[]
  locations: any[]
  conditionTypes: any[]
}

interface LineItem {
  $id: string
  item: ConnectionItem
  latlngs: number[][]
  curveID: string
}

interface MapObj {
  center: number[]
  zoom: number
}

export default Vue.extend({
  name: 'MAP',

  components: {
    scrollArea,
    popupCard
  },

  props: {
    data: {
      type: Object as () => LocationItem,
      default: () => ({})
    },
    mode: {
      type: String,
      default: () => 'location'
    },
    locations: {
      type: Array as () => LocationItem[],
      default: () => []
    },
    edit: {
      type: Boolean,
      default: false
    },
    subscribed: {
      type: Boolean,
      default: false
    },
    mapCenter: {
      type: Array as () => number[],
      default: () => [37.617589, 55.755919]
    },
    mapZoom: {
      type: Number,
      default: () => 10
    },
    editMapCenter: {
      type: Boolean,
      default: false
    }
  },

  data(): {
    map: Map | undefined
    view: View | null
    mapObj: MapObj
    markers: Feature[]
    areas: LocationItem[]
    markersFilter: any[]
    filter: FilterState
    icons: Record<string, any>
    onlyOneItem: Overlay | undefined
    down: boolean
    iconLayer: VectorLayer | null
    iconSource: VectorSource | null
    controlsLayer: VectorLayer | null
    controlsSource: VectorSource | null
    linesLayer: VectorLayer | null
    linesSource: VectorSource | null
    areasLayer: VectorLayer | null
    areasSource: VectorSource | null
    lines: LineItem[]
    lineCenters: Record<string, LineItem[]>
    selected: Feature[]
    popup: Overlay | null
    popupData: any | null
    zoom: number
    center: number[]
    url: string
    minZoom: number
    maxZoom: number
  } {
    return {
      map: undefined,
      view: null,
      mapObj: {
        center: [],
        zoom: 10
      },
      markers: [],
      areas: [],
      markersFilter: [],
      filter: {
        channelTypes: [],
        locationTypes: [],
        objectTypes: ['facility', 'building'],
        objectSubtypes: [],
        locationSubtypes: [],
        locations: [],
        conditionTypes: []
      },
      icons: {},
      onlyOneItem: undefined,
      down: false,
      iconLayer: null,
      iconSource: null,
      controlsLayer: null,
      controlsSource: null,
      linesLayer: null,
      linesSource: null,
      areasLayer: null,
      areasSource: null,
      lines: [],
      lineCenters: {},
      selected: [],
      popup: null,
      popupData: null,
      zoom: 10,
      center: [37.617589, 55.755919],
      url: '',
      minZoom: 1,
      maxZoom: 18,
    }
  },

  computed: {
    getMarkers(): Feature[] {
      if (this.filter.objectTypes.length) {
        let markers = this.markers.filter(marker => {
          const props = marker.getProperties()
          return this.filter.objectTypes.includes(props.type)
        })
        if (this.filter.conditionTypes?.length) {
          markers = markers.filter(marker => {
            const props = marker.getProperties()
            return this.filter.conditionTypes.includes(props.condition_type)
          })
        }
        if (this.filter.objectSubtypes?.length) {
          markers = markers.filter(marker => {
            const props = marker.getProperties()
            return this.filter.objectSubtypes.includes(props.location_type)
          })
        }
        return markers
      }
      return []
    },

    getAreas(): LocationItem[] {
      if (this.filter.locationTypes.length) {
        let areas = this.areas.filter(area => this.filter.locationTypes.includes(area.type || ''))
        if (this.filter.locations?.length) {
          return areas.filter(area => this.filter.locations.includes(area.$id || ''))
        }
        return areas
      }
      return []
    },
  },

  watch: {
    data(newVal: LocationItem): void {
      if (!newVal) return
      this.setup()
    },
    locations(newVal: LocationItem[]): void {
      if (!newVal?.length) return
      this.setup()
    },
    editMapCenter: {
      handler(newVal: boolean): void {
        this.centerEdit(newVal)
      },
      deep: true
    },
    '$store.state.topology.filter': {
      handler(newVal: FilterState): void {
        console.log('WATCH', this.$options.name, 'filter', 'newVal', newVal)
        if (!newVal) return
        this.$set(this, 'filter', newVal)
        if (newVal.locationTypes?.length) {
          this.addAreas(this.getAreas)
        }
        if (newVal.locationTypes && !newVal.locationTypes.length && this.areasSource) {
          this.areasSource.clear()
        }
        if (newVal.objectTypes && !newVal.objectTypes.length && this.iconSource) {
          this.iconSource.clear()
        }
      },
      deep: true
    },
    '$store.state.topology.areasShow': {
      handler(newVal: boolean): void {
        console.log('WATCH', this.$options.name, 'areasShow', newVal)
        if (!newVal) {
          this.removeAreas()
          this.closePopup()
        }
      },
      deep: true
    },
    '$store.state.topology.connectionsShow': {
      handler(newVal: boolean): void {
        console.log('WATCH', this.$options.name, 'connectionsShow', newVal)
        if (!newVal) {
          this.closePopup()
        }
      },
      deep: true
    },
    '$store.state.topology.selectedConnections': {
      handler(newVal: ConnectionItem[]): void {
        this.closePopup()
        if (newVal?.length) {
          const connectionsToShow = this.lines.filter(line => {
            return newVal.find(connection => connection.$id === line.$id)
          })
          if (connectionsToShow.length) {
            this.addLines(connectionsToShow)
          } else {
            this.closePopup()
          }
        } else {
          console.log('WATCH', this.$options.name, 'selectedConnections removeLines')
          this.closePopup()
        }
      },
      deep: true
    },
    '$store.state.topology.selectedChannels': {
      handler(newVal: any[]): void {
        console.log('WATCH', this.$options.name, 'selectedChannels', newVal)
        if (newVal?.length) {
          const channelsToShow = newVal.filter(channel => channel.show)
          if (channelsToShow.length) {
            const linesToShow: LineItem[] = []
            channelsToShow.forEach(channel => {
              if (channel.connections?.length) {
                channel.connections.forEach((connection: ConnectionItem) => {
                  const connectionLine = this.lines.find(line => line.$id === connection.$id)
                  if (connectionLine) {
                    connectionLine.channelID = channel.$id
                    linesToShow.push(connectionLine)
                  }
                })
              }
            })
            if (linesToShow.length) {
              this.addLines(linesToShow)
            }
          } else {
            this.closePopup()
          }
        } else {
          console.log('WATCH', this.$options.name, 'selectedChannels removeLines')
          this.closePopup()
        }
      },
      deep: true
    },
    center(newVal: number[]): void {
      if (!newVal) return
      const coordinates = { latitude: newVal[1], longitude: newVal[0] }
      this.$emit('change', coordinates)
    },
    zoom(newVal: number): void {
      if (!newVal) return
      console.log('WATCH', this.$options.name, 'ZOOM', newVal)
      this.$emit('changeZoom', newVal)
    },
  },

  created(): void {
    this.url = getURL('maps', '/{z}/{x}/{y}.png')
  },

  mounted(): void {
    const olMap = this.$refs.OLMap as HTMLElement
    if (olMap) {
      olMap.style.zoom = String(1 / 0.72)
    }
    this.setup()
  },

  methods: {
    ...mapActions(['openSchema']),

    async setup(): Promise<void> {
      if (this.mapCenter.length) {
        this.center = [this.mapCenter[0], this.mapCenter[1]]
        this.mapObj.center = [this.mapCenter[0], this.mapCenter[1]]
      }

      if (!this.locations.length) {
        const data = this.data as LocationItem
        if (data.coordinates?.latitude && data.coordinates?.longitude) {
          this.center = [data.coordinates.longitude, data.coordinates.latitude]
        } else if (data.latitude && data.longitude) {
          this.center = [data.longitude, data.latitude]
        }
      }

      if (this.mapZoom) {
        this.zoom = this.mapZoom
        this.mapObj.zoom = this.mapZoom
      }

      const markers = this.locations.length ? this.locations : [this.data]

      this.statusUnsubscribe('OL_MAP_PATH')
      await this.createMarkers(markers)
      await this.createLines()
      await this.initMap()
      await this.addLines(this.lines)
      if (this.editMapCenter) {
        this.centerEdit(true)
      }
    },

    async initMap(): Promise<void> {
      this.linesSource = new VectorSource({ wrapX: false })
      this.controlsSource = new VectorSource({ wrapX: false })
      this.areasSource = new VectorSource({ wrapX: false })
      this.linesLayer = new VectorLayer({ source: this.linesSource })
      this.controlsLayer = new VectorLayer({ source: this.controlsSource })
      this.areasLayer = new VectorLayer({ source: this.areasSource })
      this.iconSource = new VectorSource({
        features: this.markers,
        crossOrigin: 'Anonymous',
      })
      this.iconLayer = new VectorLayer({
        source: this.iconSource,
        crossOrigin: 'Anonymous',
      })

      this.areasLayer.setZIndex(1)
      this.linesLayer.setZIndex(3)
      this.controlsLayer.setZIndex(3)
      this.iconLayer.setZIndex(5)

      const popupElement = this.$refs['connection-popup'] as HTMLElement

      const rasterLayer = new TileLayer({
        source: new XYZ({
          url: getURL('maps', '/{z}/{x}/{y}.png'),
          wrapX: true,
          crossOrigin: 'Anonymous'
        }),
      })

      this.view = new View({
        center: fromLonLat(this.center),
        zoom: this.zoom,
        minZoom: 1,
        maxZoom: 25,
      })

      const select = new interaction.Select({
        hitTolerance: 5,
        wrapX: false,
        style: null,
      })
      select.dispatchEvent('select')
      console.log('select', select)

      this.map = new Map({
        interactions: interaction.defaults().extend([select]),
        layers: [rasterLayer, this.iconLayer, this.linesLayer, this.controlsLayer, this.areasLayer],
        target: this.$refs.OLMap as HTMLElement,
        view: this.view,
      })

      this.map.getView().animate({
        zoom: this.zoom,
        duration: 250
      })

      this.map.getView().on('change:resolution', () => {
        if (this.map) {
          this.mapObj.zoom = this.map.getView().getZoom() || 10
        }
      })

      this.map.on('pointerdown', (e: any) => {
        if (this.editMapCenter && e.originalEvent.target.className === 'centerIcon') {
          this.down = true
          this.onlyOneItem = this.map?.getOverlayById('centerIcon')
          return false
        }
        return false
      })

      this.map.on('pointerup', (e: any) => {
        if (this.editMapCenter && e.originalEvent.target.className === 'centerIcon') {
          this.onlyOneItem = undefined
          this.down = false
          this.$emit('changeCenter', this.mapObj)
          return false
        }
        return false
      })

      this.map.on('pointermove', (evt: any) => {
        const pixel = this.map?.getEventPixel(evt.originalEvent)
        const hit = this.map?.hasFeatureAtPixel(pixel)
        if (this.map) {
          this.map.getTarget().style.cursor = hit ? 'pointer' : ''
        }
        if (this.down && this.onlyOneItem) {
          const coordinate = this.map?.getCoordinateFromPixel([evt.pixel[0] + (this.onlyOneItem.element.firstChild?.offsetWidth / 4), evt.pixel[1]])
          if (coordinate) {
            this.onlyOneItem.setPosition(coordinate)
            const trueCoord = toLonLat(this.onlyOneItem.getPosition())
            this.center = [trueCoord[0], trueCoord[1]]
            this.mapObj.center = [trueCoord[0], trueCoord[1]]
          }
        }
      })

      select.on('select', (e: any) => {
        if (!e.selected.length && !e.deselected.length) return

        if (e.selected.length) {
          const prop = e.selected[0].getProperties()
          const type = prop.geometry.getType()

          if (type === 'Point') {
            this.onMarkerOpen(prop.id)
          }
          if (type === 'LineString') {
            const id = e.selected[0].get('id')
            this.createOverlayConnection(e, id)
            this.selected.push(e.selected[0])
            const status = this.$store.state.monitoring.status[e.selected[0].getProperties().id] || '#ff9f19'
            e.selected[0].setStyle(
              new Style({
                stroke: new Stroke({
                  color: this.hexToRgbA(this.getStatus(status)),
                  width: 4
                })
              })
            )
          }
          if (type === 'Polygon') {
            const id = e.selected[0].get('id')
            this.createOverlayArea(e, id)
            this.selected.push(e.selected[0])
            const area = this.$store.state.topology.areas.find((a: LocationItem) => a.$id === id)
            const status = this.$store.state.monitoring.status[e.selected[0].getProperties().id] || '#ff9f19'
            e.selected[0].setStyle(
              new Style({
                stroke: new Stroke({
                  color: this.hexToRgbA(this.getStatus(status)),
                  width: 4
                }),
                fill: new Fill({
                  color: area?.area_color ? area.area_color + '85' : '#2fb13f85',
                }),
              })
            )
          }
        }

        if (e.deselected.length) {
          const prop = e.deselected[0].getProperties()
          const type = prop.geometry.getType()

          if (type === 'Point') {
            if (!e.selected.length) {
              this.closePopup()
            }
          }
          if (type === 'LineString') {
            const id = e.deselected[0].get('id')
            const status = this.$store.state.monitoring.status[e.deselected[0].getProperties().id] || '#ff9f19'
            e.deselected[0].setStyle(
              new Style({
                stroke: new Stroke({
                  color: this.hexToRgbA(this.getStatus(status)),
                  width: 2
                })
              })
            )
            if (!e.selected.length) {
              this.closePopup()
            }
          }
          if (type === 'Polygon') {
            const id = e.deselected[0].get('id')
            const area = this.$store.state.topology.areas.find((a: LocationItem) => a.$id === id)
            const status = this.$store.state.monitoring.status[e.deselected[0].getProperties().id] || '#ff9f19'
            e.deselected[0].setStyle(
              new Style({
                stroke: new Stroke({
                  color: this.hexToRgbA(this.getStatus(status)),
                  width: 2
                }),
                fill: new Fill({
                  color: area?.area_color ? area.area_color + '85' : '#2fb13f85',
                }),
              })
            )
            if (!e.selected.length) {
              this.closePopup()
            }
          }
        }
      })

      this.popup = new Overlay({
        element: popupElement,
        positioning: 'bottom-center',
      })
      this.popup.panIntoView()

      this.popup.on('mousewheel', (e: any) => {
        e.browserEvent.preventDefault()
      })
    },

    createOverlayConnection(e: any, id: string): void {
      const connection = this.$store.getters.getConnections.find((c: ConnectionItem) => c.$id === id)
      this.popupData = connection
      this.popup?.setPosition(e.mapBrowserEvent.coordinate)
      if (this.map && this.popup) {
        this.map.addOverlay(this.popup)
      }
    },

    createOverlayArea(e: any, id: string): void {
      const area = this.$store.state.topology.areas.find((a: LocationItem) => a.$id === id)
      this.popupData = area
      this.popup?.setPosition(e.mapBrowserEvent.coordinate)
      if (this.map && this.popup) {
        this.map.addOverlay(this.popup)
      }
    },

    closePopup(): void {
      if (!this.popup) return
      this.popup.setPosition(undefined)
      this.popupData = null
    },

    async createLines(): Promise<void> {
      console.log(this.$options.name, 'createLines length', this.$store.getters.getConnections.length)
      if (!this.$store.getters.getConnections.length) return

      const linesCoords: LineItem[] = []
      const arr = this.$store.getters.getConnections
      this.statusSubscribe(arr, 'OL_MAP_PATH')

      arr.forEach((connection: ConnectionItem) => {
        if (!connection.locations?.[0] || !connection.locations?.[1]) {
          console.log('no locations', connection)
          return
        }

        const line: LineItem = {
          $id: connection.$id || '',
          item: connection,
          latlngs: [],
          curveID: ''
        }

        const loc0 = connection.locations[0]
        const loc1 = connection.locations[1]

        if (loc0.coordinates) {
          line.latlngs[0] = [Number(loc0.coordinates.longitude), Number(loc0.coordinates.latitude)]
        } else if (loc0.latitude && loc0.longitude) {
          line.latlngs[0] = [Number(loc0.longitude), Number(loc0.latitude)]
        }

        if (loc1.coordinates) {
          line.latlngs[1] = [Number(loc1.coordinates.longitude), Number(loc1.coordinates.latitude)]
        } else if (loc1.latitude && loc1.longitude) {
          line.latlngs[1] = [Number(loc1.longitude), Number(loc1.latitude)]
        }

        if (turf.distance(line.latlngs[0], line.latlngs[1]) === 0) {
          console.log(this.$options.name, 'NO DISTANCE', turf.distance(line.latlngs[0], line.latlngs[1]))
          return
        }

        const a = [
          line.latlngs[0][0].toFixed(3),
          line.latlngs[0][1].toFixed(3),
          line.latlngs[1][0].toFixed(3),
          line.latlngs[1][1].toFixed(3)
        ]
        a.sort((a, b) => parseFloat(a) - parseFloat(b)).forEach((item) => {
          line.curveID = line.curveID + item + '$'
        })

        if (this.lineCenters[line.curveID]) {
          this.lineCenters[line.curveID].push(line)
        } else {
          this.lineCenters[line.curveID] = [line]
        }

        linesCoords.push(line)
      })

      if (linesCoords.length) {
        await this.createCurves(linesCoords)
      }
    },

    async createCurves(arr: LineItem[]): Promise<void> {
      arr.forEach(line => {
        const lineIndex = this.lineCenters[line.curveID].indexOf(line)
        if (lineIndex === 0) {
          this.lines.push(line)
          return
        }

        const p = turf.points(line.latlngs)
        const midpointCoords = turf.center(p)
        const line_ = turf.lineString([midpointCoords.geometry.coordinates, line.latlngs[0]])

        const r = lineIndex % 2 === 0 ? 90 : -90
        const options = { pivot: line_.geometry.coordinates[0] }
        const rotatedPoly = turf.transformRotate(line_, r, options)
        const normal = turf.clone(rotatedPoly)

        const distance = turf.distance(normal.geometry.coordinates[0], normal.geometry.coordinates[1])
        const along = turf.along(normal, distance / 10 * (lineIndex % 2 === 0 ? lineIndex - 1 : lineIndex))

        line.latlngs.splice(1, 0, along.geometry.coordinates)
        this.lines.push(line)
      })
    },

    factorial(num: number): number {
      if (num <= 1) return 1
      return num * this.factorial(num - 1)
    },

    getCoordinatesBezier(controlPoints: Feature[], t: number): number[] {
      let x = 0
      let y = 0
      const n = controlPoints.length - 1
      controlPoints.forEach((item, index) => {
        const coord = item.getGeometry()?.getFirstCoordinate()
        if (!coord) return
        if (!index) {
          x += coord[0] * Math.pow((1 - t), n - index) * Math.pow(t, index)
          y += coord[1] * Math.pow((1 - t), n - index) * Math.pow(t, index)
        } else {
          x += this.factorial(n) / this.factorial(index) / this.factorial(n - index) * coord[0] * Math.pow((1 - t), n - index) * Math.pow(t, index)
          y += this.factorial(n) / this.factorial(index) / this.factorial(n - index) * coord[1] * Math.pow((1 - t), n - index) * Math.pow(t, index)
        }
      })
      return [x, y]
    },

    genBezierGeom(controlPoints: Feature[], step: number | null): any {
      const nodeArr = controlPoints.sort((a, b) => {
        return a.get('cid') - b.get('cid')
      })
      if (nodeArr.length === 2) {
        const lineId = nodeArr[0].getProperties().id
        return turf.lineString([nodeArr[0].getGeometry()?.getFirstCoordinate(), nodeArr[1].getGeometry()?.getFirstCoordinate()], { id: lineId })
      } else {
        const lineId = nodeArr[0].getProperties().id
        const bezierPoints: number[][] = []
        for (let i = 0; i < 1; i += (step !== null ? step : 0.02)) {
          bezierPoints.push(this.getCoordinatesBezier(nodeArr, i))
        }
        return turf.lineString(bezierPoints, { id: lineId })
      }
    },

    async addLines(arr: LineItem[]): Promise<void> {
      for (const line of arr) {
        const ctrlFeatures: Feature[] = []
        line.latlngs.forEach((item, index) => {
          const ctrlPointFeature = new Feature({
            cid: index,
            id: line.$id,
            geometry: new Point(fromLonLat(item)),
            data: item
          })
          ctrlPointFeature.setStyle(
            new Style({
              image: new Circle({
                radius: 5,
                fill: new Fill({ color: [255, 255, 255, 0.5] }),
                stroke: new Stroke({ color: [122, 122, 122, 1], width: 2 })
              })
            })
          )
          ctrlFeatures.push(ctrlPointFeature)
        })

        const n = new GeoJSON().readFeature(this.genBezierGeom(ctrlFeatures, 0.1))
        const status = this.$store.state.monitoring.status[n.getProperties().id] || '#ff9f19'
        n.setStyle(
          new Style({
            stroke: new Stroke({
              color: this.hexToRgbA(this.getStatus(status)),
              width: 2
            })
          })
        )
        n.setId(line.$id)
        this.linesSource?.addFeature(n)
      }
    },

    async addMarkers(arr: Feature[]): Promise<void> {
      if (this.iconSource) {
        this.iconSource.clear()
      }
      for (const marker of arr) {
        this.iconSource?.addFeatures([marker])
      }
    },

    async addAreas(arr: LocationItem[]): Promise<void> {
      if (this.areasSource) {
        this.areasSource.clear()
      }
      for (const area of arr) {
        if (area.finalyPoligons) {
          this.areasSource?.addFeatures(area.finalyPoligons)
        }
      }
    },

    hexToRgbA(hex: string, alpha: number = 1): number[] {
      const matches = hex.match(/\w\w/g)
      if (!matches) return [0, 0, 0, alpha]
      const [r, g, b] = matches.map(x => parseInt(x, 16))
      return [r, g, b, alpha]
    },

    async removeLines(id?: string): Promise<void> {
      if (this.linesSource) {
        this.linesSource.clear()
      }
    },

    async removeAreas(id?: string): Promise<void> {
      if (this.areasSource) {
        this.areasSource.clear()
      }
    },

    async createOverlayItem(items: any[], onlyOne: boolean): Promise<void> {
      console.log('createOverlayItem items', items)
      for (let i = 0; i < items.length; i++) {
        const existingOverlay = this.map?.getOverlayById(items[i].id)
        if (existingOverlay) {
          this.map?.removeOverlay(existingOverlay)
        }

        const element = this.$refs[items[i].id] as HTMLElement | HTMLElement[]
        const targetElement = onlyOne ? element : (element as HTMLElement[])[0]

        if (this.subscribed && targetElement) {
          targetElement.style.backgroundImage = await this.checkIcon(items[i])
        }

        const popup = new Overlay({
          element: targetElement,
          positioning: 'bottom-center',
          stopEvent: false,
          name: items[i].users_name,
          id: items[i].id,
        })
        popup.panIntoView()
        popup.setPosition(fromLonLat(items[i].coord))
        this.map?.addOverlay(popup)
      }
    },

    async removeOverlayItem(items: any[]): Promise<void> {
      for (let i = 0; i < items.length; i++) {
        const overlay = this.map?.getOverlayById(items[i].id)
        if (overlay) {
          this.map?.removeOverlay(overlay)
        }
      }
    },

    centerEdit(val: boolean): void {
      if (val) {
        const marker = {
          users_name: 'center',
          id: 'centerIcon',
          type: 'marker',
          coord: [String(this.center[0]), String(this.center[1])],
        }
        this.createOverlayItem([marker], true)
      }
    },

    async createMarkers(arr: LocationItem[]): Promise<void> {
      this.statusSubscribe(arr, 'OL_MAP_PATH')
      this.markers = []

      for (const item of arr) {
        let coordinates: number[] = []
        if (item.coordinates) {
          coordinates = [item.coordinates.longitude, item.coordinates.latitude]
        } else if (item.latitude && item.longitude) {
          coordinates = [item.longitude, item.latitude]
        }

        if (coordinates.length) {
          const iconFeature = new Feature({
            geometry: new Point(fromLonLat(coordinates)),
            name: 'Null Island',
            population: 4000,
            rainfall: 500,
            id: item.$id,
            crossOrigin: 'Anonymous',
            obj: item,
            type: item.type,
            condition_type: item.condition_type,
            location_type: item.location_type
          })

          const status = this.$store.state.monitoring.status[item.$id || ''] || '#3399CC'
          const iconStyle = new Style({
            image: new Circle({
              radius: 8,
              stroke: new Stroke({ color: '#212121', width: 2 }),
              fill: new Fill({ color: this.getStatus(status) }),
            }),
            text: new Text({
              text: item.name || '',
              offsetY: 20,
              fill: new Fill({ color: '#333b4a' }),
              font: '14px roboto',
              backgroundFill: new Fill({ color: '#FFFFAF' }),
              padding: [3, 3, 3, 3]
            })
          })

          iconFeature.setId(item.$id)
          iconFeature.setStyle(iconStyle)
          this.markers.push(iconFeature)
        }
      }
      console.log('getMarkers', this.getMarkers)
    },

    async createAreas(arr: LocationItem[]): Promise<void> {
      this.statusSubscribe(arr, 'OL_MAP_PATH')
      this.areas = []

      arr.forEach((item$, index) => {
        const item = this.$merge({}, item$)
        if (item.area?.[0]) {
          let END = 0
          this.$set(item, 'finalyPoligons', [])

          for (let i = 0; i < item.area.length; i++) {
            const poly = item.area[i]
            const finalPoly: number[] = []

            for (let c = 0; c < poly.length; c++) {
              let preDone = poly[c]
              preDone = [preDone.longitude, preDone.latitude]
              preDone = fromLonLat(preDone)
              finalPoly.push(preDone[0])
              finalPoly.push(preDone[1])

              if (c === poly.length - 1) {
                END++
                const geometry = new Polygon(finalPoly, 'XY', [finalPoly.length])
                const feature = new Feature({
                  geometry: geometry,
                  id: item.$id,
                })

                const status = this.$store.state.monitoring.status[item.$id || ''] || '#ff9f19'
                feature.setStyle(new Style({
                  fill: new Fill({ color: item.area_color ? item.area_color + '85' : '#2fb13f85' }),
                  stroke: new Stroke({
                    color: this.hexToRgbA(this.getStatus(status)),
                    width: 3,
                  })
                }))

                item.finalyPoligons.push(feature)
                if (END === item.area.length) {
                  this.$set(this.areas, index, item)
                }
              }
            }
          }
        }
      })
    },

    async onMarkerOpen(id: string): Promise<void> {
      if (this.mode === 'topology') {
        this.openModal(id)
      }
      if (this.mode === 'location') {
        console.log('location marker', id)
        if (this.edit) {
          if (this.down) {
            this.onlyOneItem = undefined
          } else {
            this.onlyOneItem = this.map?.getOverlayById(id)
          }
          this.down = !this.down
        }
      }
    },

    async openModal(id: string): Promise<void> {
      const obj$ = await this.$bridge.getObjectById(id)
      console.log('STORE', 'setDialog obj$', obj$)
      if (obj$) {
        const dialogData = {
          component: 'mapModal',
          id: id,
          title: obj$.name,
          data: obj$,
          options: {
            width: (this.$el as HTMLElement).clientWidth * 0.8,
            height: (this.$el as HTMLElement).clientHeight * 0.7,
            top: 100
          }
        }
        this.$store.dispatch('setDialog', dialogData)
      }
    },

    filepath(type: string): string {
      return require(`@/nms/components/media/svg/static/files/${type}.svg`)
    },

    getIconUrl(marker: LocationItem): string {
      const type$ = marker.location_type || marker.type
      return require(`@/nms/components/media/svg/static/files/${type$}.svg`)
    },

    async checkIcon(marker: LocationItem): Promise<string> {
      const type$ = marker.location_type || marker.type
      const url$ = this.filepath(type$ || 'default')
      const text$ = await fetch(url$).then(r => r.text())
      const svgTree = parse(text$)
      const status = this.$store.state.monitoring.status[marker.$id || ''] || '#ff9f19'
      svgTree.each('path', (node: any) => {
        node.attrs.fill = this.getStatus(status)
      })
      return render(svgTree)
    },

    getRandomColor(): string {
      const letters = '0123456789ABCDEF'
      let color = '#'
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
      }
      return color
    },

    getStatus(status: string): string {
      if (this.$store.state.books.statuses.length) {
        let color = this.$store.state.books.statuses.find((item: any) => item.name === status)
        if (!color) {
          color = this.$store.state.books.statuses.find((item: any) => item.name === 'undefined')
        }
        return color?.back_color || '#3399CC'
      }
      return '#3399CC'
    },

    async statusSubscribe(arr: any[], group: string): Promise<void> {
      this.$store.dispatch('measuresSubscribeInit', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })

      if (arr.length) {
        await this.$bridge.subscribe(arr.map(obj => obj.$id))
      }
    },

    async subscribeHandler(e: any): Promise<void> {
      this.$store.dispatch('addStatus', e)

      if (e?.$id) {
        const cls = e.$id.split('$')[0]

        if (cls === 'connection') {
          const f = await this.linesSource?.getFeatureById(e.$id)
          if (f) {
            const status$ = e.state?.infoState || e.state || 'unknown'
            f.setStyle(
              new Style({
                stroke: new Stroke({
                  color: this.hexToRgbA(this.getStatus(status$)),
                  width: 2
                })
              })
            )
          }
        }

        if (cls === 'location') {
          const f = await this.iconSource?.getFeatureById(e.$id)
          if (f) {
            const obj = f.get('obj')
            const status$ = e.state?.infoState || e.state || 'unknown'

            const iconStyle = new Style({
              image: new Circle({
                radius: 8,
                stroke: new Stroke({ color: '#212121', width: 2 }),
                fill: new Fill({ color: this.hexToRgbA(this.getStatus(status$)) }),
              }),
              text: new Text({
                text: obj?.name || '',
                offsetY: 20,
                fill: new Fill({ color: '#333b4a' }),
                font: '14px roboto',
                backgroundFill: new Fill({ color: '#FFFFAF' }),
                padding: [3, 3, 3, 3]
              })
            })
            f.setStyle(iconStyle)
          }
        }
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
    },

    getMirror(arr: any[]): void {
      for (const item of arr) {
        if (item.$id) {
          this.$store.dispatch('addStatus', item)
        }
        if (item.children?.length) {
          this.getMirror(item.children)
        }
      }
    },
  },
})
</script>

<style lang="css" scoped>

.location{
    /* background-color: #f7941d; */
    /* background-image:  url('../../../assets/icon/location.svg');                                                              */
    /* mask-image:  url('../../../assets/icon/well-active.svg');                                                              */
    background-repeat: no-repeat;                                                              
    height:48px;
    width: 48px;
    cursor: pointer;
    position: absolute;
    top: -43px;
    left: -24px;
}
.centerIcon{
    /* background-color: #f7941d; */
    background-image:  url('../../assets/icon/marker.png'); 
    background-size: contain;
    background-position: center center;                                                            
    /* background-image:  url('../../../assets/icon/location.svg');                                                              */
    /* mask-image:  url('../../../assets/icon/well-active.svg');                                                              */
    background-repeat: no-repeat;                                                              
    height:48px;
    width: 48px;
    cursor: pointer;
    position: absolute;
    top: -43px;
    left: -24px;
}
.onlineIcon{
  position: absolute;
  height:26px;
  width: 26px;
  top: 5px;
  right: 2px;
}
.popup-content {
  position: relative;
  /* min-width: 460px; */
  min-height: 480px;
  /* max-width: 460px; */
  max-height: 480px;
}
.ol-popup {
  position: absolute;
  background-color: white;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  padding: 4px;
  border-radius: 4px;
  border: 1px solid #cccccc;
  bottom: 12px;
  left: -50px;
  min-width: 680px;
  min-height: 480px;
  max-width: 680px;
  max-height: 480px;
}
.ol-popup:after, .ol-popup:before {
  top: 100%;
  border: solid transparent;
  content: " ";
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
}
.ol-popup:after {
  border-top-color: white;
  border-width: 10px;
  left: 48px;
  margin-left: -10px;
}
.ol-popup:before {
  border-top-color: #cccccc;
  border-width: 11px;
  left: 48px;
  margin-left: -11px;
}
.description-icon-obj{
  min-width: 78px;
  min-height: 20px;
  background-color: #FFFFAF;
  border-radius: 10px;
  padding: 3px;
  position: absolute;
  top: 50px;
  left: -15px;
  float: left
}

</style>
